import { createPortal } from 'react-dom';

const SEEN_KEY = 'yoga-me.intro.seen.v1';

export function introSeen(): boolean {
  try {
    return localStorage.getItem(SEEN_KEY) === '1';
  } catch {
    return false; // blocked storage — showing the intro is the safe default
  }
}

export function markIntroSeen(): void {
  try {
    localStorage.setItem(SEEN_KEY, '1');
  } catch {
    /* best effort — it just reappears next visit */
  }
}

interface Step {
  title: string;
  body: (poseCount: number) => string;
}

// Three steps because that's the whole loop: learn a pose, string poses into a
// routine, run the routine without looking at the screen. The last one is the
// point of the app and the least discoverable, so it earns the final slot.
const STEPS: Step[] = [
  {
    title: 'Learn the poses',
    body: (n) =>
      `Plain-language cues, benefits and modifications for ${n} poses. Mark each Practiced, then Learned, as it sinks in.`,
  },
  {
    title: 'Build a routine',
    body: () =>
      'Pick poses and set the holds — two-sided poses run both sides for you. Or start from the built-in Sun Salutation.',
  },
  {
    title: 'Press play, then look away',
    body: () =>
      'The player names each pose, counts the hold down and calls the switch — so you can set the phone down and move.',
  },
];

function Steps({ poseCount }: { poseCount: number }) {
  return (
    <ol className="how-steps">
      {STEPS.map((s, i) => (
        <li className="how-step" key={s.title}>
          <span className="how-num" aria-hidden="true">
            {i + 1}
          </span>
          <div>
            <h3 className="how-step-title">{s.title}</h3>
            <p className="how-step-body">{s.body(poseCount)}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * First-visit intro, shown above the page until it's waved off. Deliberately
 * inline rather than a modal on load — nothing is blocked, and the poses stay
 * visible behind it.
 */
export function HowItWorksIntro({
  poseCount,
  onDismiss,
}: {
  poseCount: number;
  onDismiss: () => void;
}) {
  return (
    <aside className="how-card" aria-labelledby="how-title">
      <h2 className="how-title" id="how-title">
        New here? Yoga Me in three steps
      </h2>
      <Steps poseCount={poseCount} />
      <button type="button" className="btn primary how-got-it" onClick={onDismiss}>
        Got it
      </button>
      <button type="button" className="cta-dismiss" onClick={onDismiss} aria-label="Dismiss">
        ×
      </button>
    </aside>
  );
}

/** The same three steps, reachable again any time from the account menu. */
export function HowItWorksModal({
  poseCount,
  onClose,
}: {
  poseCount: number;
  onClose: () => void;
}) {
  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal how-modal"
        role="dialog"
        aria-modal="true"
        aria-label="How Yoga Me works"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>
        <h2 className="auth-title">How Yoga Me works</h2>
        <Steps poseCount={poseCount} />
      </div>
    </div>,
    document.body,
  );
}
